import ButtonHelpTooltip from 'components/Tooltip/ButtonHelpTooltip';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';



import arrowDownIcon from '../../../assets/img/icons/arrow-down.svg';
import rankBadgeIcon from '../../../assets/img/icons/rank-badge.svg';
import { TopPerformerInfoContainer, TopPerformerRankBadge, TopPerformerRankBadgeImage, TopPerformerRankText, TopPerformerRow, TopPerformerStatsContainer, TopPerformerStatsText, TopPerformersContainer, TopPerformersDropdownContainer, TopPerformersDropdownOption, TopPerformersHeader, TopPerformersListContainer, TopPerformersSelectContainer, TopPerformersSelectIcon, TopPerformersSelectText, TopPerformersSkeletonAvatar, TopPerformersSkeletonName, TopPerformersSkeletonRankBadge, TopPerformersSkeletonRow, TopPerformersSkeletonSelect, TopPerformersSkeletonStats, TopPerformersSkeletonTitle, TopPerformersTitle } from "../../DashBoardNew/dashBoardNewStyles";
import { CommImageNameComp } from '../../Members/CommonComponents';


const TopPerformers = ({
  topPerformers,
  selectedOrganization,
  getDashboardPerformers,
  isLoading,
  selectedPeriod: _selectedPeriod = 'This Week',
  periodOptions = ['This Week', 'This Month', 'This Year'],
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(_selectedPeriod);
  const [performers, setPerformers] = useState([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const orgType = selectedOrganization?.type || 'operation';
  const isOperationType = orgType === 'operation';

  // Transform API data to component format
  const transformPerformersData = (apiData) => {
    if (!apiData || !apiData.top_performers || !Array.isArray(apiData.top_performers)) {
      return [];
    }

    return apiData.top_performers.slice(0, 5).map((performer, index) => {
      const fullName = `${performer.first_name || ''} ${performer.last_name || ''}`.trim();
      const jobsCompleted = performer.jobs_completed || 0;

      return {
        rank: index + 1,
        name: fullName || performer.email || 'Unknown',
        avatar: performer.avatar || performer.image || null,
        stats: `${jobsCompleted} ${jobsCompleted === 1 ? (isOperationType ? 'task' : 'job') : (isOperationType ? 'tasks' : 'jobs')} done`,
        index: index % 4, // For color cycling (0-3)
        color: performer.color || null,
      };
    });
  };

  // Call API when period or organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardPerformers) {
      let startTime, endTime;

      if (selectedPeriod === 'This Week') {
        startTime = moment().startOf('week').format('YYYY-MM-DD');
        endTime = moment().endOf('week').format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This Month') {
        startTime = moment().startOf('month').format('YYYY-MM-DD');
        endTime = moment().endOf('month').format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This Year') {
        startTime = moment().startOf('year').format('YYYY-MM-DD');
        endTime = moment().endOf('year').format('YYYY-MM-DD');
      }

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };

      getDashboardPerformers(payload);
    }
  }, [selectedOrganization, selectedPeriod, getDashboardPerformers]);

  // Update performers when API data changes
  useEffect(() => {
    if (topPerformers) {
      const transformedData = transformPerformersData(topPerformers);
      setPerformers(transformedData);
    } else {
      setPerformers([]);
    }
  }, [topPerformers]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen]);

  const handleSelectClick = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handlePeriodSelect = (period) => {
    setIsSelectOpen(false);
    setSelectedPeriod(period);
  };

  return (
     <ButtonHelpTooltip
    transform='translateX(-50%) translateY(100%)'
      enabled={true}
      text="List of Employees who have worked the most this week/month/year."
    >
    <TopPerformersContainer>
      <TopPerformersHeader>
        {isLoading ? (
          <>
            <TopPerformersSkeletonTitle />
            <TopPerformersSkeletonSelect />
          </>
        ) : (
          <>
            <TopPerformersTitle>Top Performers</TopPerformersTitle>
            <TopPerformersSelectContainer ref={selectRef} onClick={handleSelectClick}>
              <TopPerformersSelectText>{selectedPeriod}</TopPerformersSelectText>
              <TopPerformersSelectIcon src={arrowDownIcon} alt="Dropdown" />
              {isSelectOpen && (
                <TopPerformersDropdownContainer ref={dropdownRef}>
                  {periodOptions.map((option) => (
                    <TopPerformersDropdownOption
                      key={option}
                      isSelected={option === selectedPeriod}
                      onClick={() => handlePeriodSelect(option)}
                    >
                      {option}
                    </TopPerformersDropdownOption>
                  ))}
                </TopPerformersDropdownContainer>
              )}
            </TopPerformersSelectContainer>
          </>
        )}
      </TopPerformersHeader>

      <TopPerformersListContainer>
        {isLoading ? (
          // Skeleton loading state - show 5 skeleton rows
          Array.from({ length: 5 }).map((_, index) => (
            <TopPerformersSkeletonRow key={`skeleton-${index}`}>
              <TopPerformersSkeletonRankBadge />
              <TopPerformerInfoContainer>
                <TopPerformersSkeletonAvatar />
                <TopPerformersSkeletonName />
              </TopPerformerInfoContainer>
              <TopPerformerStatsContainer>
                <TopPerformersSkeletonStats />
              </TopPerformerStatsContainer>
            </TopPerformersSkeletonRow>
          ))
        ) : (
          // Always show 5 rows with rank badges
          Array.from({ length: 5 }).map((_, index) => {
            const performer = performers[index];
            return (
              <TopPerformerRow key={index + 1}>
                <TopPerformerRankBadge>
                  <TopPerformerRankBadgeImage src={rankBadgeIcon} alt="Rank badge" />
                  <TopPerformerRankText>{index + 1}</TopPerformerRankText>
                </TopPerformerRankBadge>
                <TopPerformerInfoContainer>
                  {performer ? (
                    <CommImageNameComp
                      imgSource={performer.avatar || ''}
                      name={performer.name}
                      size="24px"
                      fontSize="13px"
                      index={performer.index}
                      color={performer.color}
                      showAddDesignation={false}
                    />
                  ) : (
                    <span style={{ 
                      fontSize: '13px',
                      color: '#9ca3af',
                      letterSpacing: '1px'
                    }}>
                      -------------------------------
                    </span>
                  )}
                </TopPerformerInfoContainer>
                <TopPerformerStatsContainer>
                  {performer ? (
                    <TopPerformerStatsText>{performer.stats}</TopPerformerStatsText>
                  ) : (
                    <span style={{ 
                      fontSize: '13px',
                      color: '#9ca3af',
                      letterSpacing: '1px'
                    }}>
                      --
                    </span>
                  )}
                </TopPerformerStatsContainer>
              </TopPerformerRow>
            );
          })
        )}
      </TopPerformersListContainer>
    </TopPerformersContainer>
    </ButtonHelpTooltip>
  );
};

export default TopPerformers;